import { isDevPreview } from '../boot/devPreview';
import { sb } from './supabase';
import { logActivity } from './repo';
import { canTransition } from './versionRepo';
import type { VersionStatus } from './types';

/**
 * 협업 저장소 — 댓글 · 승인 · 활동 로그 (작업지시서 P4-11 / P4-12 / P4-14)
 *
 * ── 이 계층이 지키는 것 ─────────────────────────────────────────────────
 *
 *   **승인 흐름은 화면과 DB 양쪽에서 막는다.**
 *   화면 검증만 두면 API 를 직접 부르는 순간 뚫린다. 반대로 DB 만 두면
 *   사용자는 "왜 안 되는지" 를 못 본다. 둘 다 필요하다.
 *
 *   승인 이력(cad_approval)은 상태를 바꿀 때마다 **반드시** 남긴다.
 *   나중에 "누가 이걸 승인했나" 에 답하지 못하면 감사에서 문제가 된다.
 */

export type CommentAnchor =
  | { type: 'point'; x: number; y: number }
  | { type: 'rect'; x: number; y: number; w: number; h: number };

export interface CommentRow {
  id: string;
  version_id: string;
  parent_id: string | null;
  entity_id: string | null;
  anchor: CommentAnchor | null;
  body: string;
  mentions: string[];
  is_change_request: boolean;
  status: 'open' | 'resolved';
  created_by: string;
  created_at: string;
  resolved_by: string | null;
  resolved_at: string | null;
}

export type ApprovalAction =
  | 'request'
  | 'approve'
  | 'reject'
  | 'changes_requested'
  | 'publish'
  | 'unpublish'
  | 'archive';

export interface ApprovalRow {
  id: string;
  version_id: string;
  action: ApprovalAction;
  from_status: VersionStatus | null;
  to_status: VersionStatus | null;
  reason: string | null;
  actor: string;
  created_at: string;
}

function fail(where: string, error: { message: string } | null): void {
  if (error) throw new Error(`${where}: ${error.message}`);
}

/* -------------------------------------------------------------------------- *
 * 댓글 (P4-11)
 * -------------------------------------------------------------------------- */

export interface MentionCandidate {
  email: string;
  name: string;
}

/**
 * 본문에서 멘션을 뽑는다.
 *
 * 사용자는 `@이름` 을 입력한다 (메일 주소를 몰라도 되게). 저장·알림은
 * 여전히 이메일 기준이라, 등록된 사람 목록(`directory`)과 대조해서
 * `@이름` → 이메일로 바꾼다. 대조할 목록이 없으면(과거 형식 호환) 옛
 * `@메일주소` 형태도 계속 인정한다.
 *
 * ★ 이름은 "정확히 일치" 할 때만 멘션으로 인정한다 — @평대 같은 일반
 *   문장을 멘션으로 오인하면 안 된다. 화면 쪽 자동완성이 정확한 이름을
 *   골라 넣어 주므로, 자유 입력이어도 오탐이 드물다.
 */
export function extractMentions(body: string, directory: MentionCandidate[] = []): string[] {
  const out = new Set<string>();
  // 메일 형태 (과거 댓글·외부 연동 호환)
  for (const m of body.matchAll(/@([\w.+-]+@[\w-]+\.[\w.-]+)/g)) {
    out.add(m[1]!.toLowerCase());
  }
  // 이름 형태 — 긴 이름부터 검사해 "김민" 이 "김민수" 안에서 잘못 걸리지 않게 한다
  const byNameLength = [...directory].sort((a, b) => b.name.length - a.name.length);
  for (const { email, name } of byNameLength) {
    if (!name) continue;
    const idx = body.indexOf(`@${name}`);
    if (idx === -1) continue;
    const after = body[idx + 1 + name.length];
    if (after === undefined || /[\s,.!?)\]}~·]/.test(after)) {
      out.add(email.toLowerCase());
    }
  }
  return [...out];
}

export async function listComments(versionId: string): Promise<CommentRow[]> {
  if (isDevPreview()) return devComments(versionId);

  const client = sb();
  const { data, error } = await client
    .from('cad_comment')
    .select('*')
    .eq('version_id', versionId)
    .order('created_at', { ascending: true });
  fail('댓글 조회', error);

  return (data ?? []) as CommentRow[];
}

export interface NewComment {
  versionId: string;
  body: string;
  parentId?: string | null;
  entityId?: string | null;
  anchor?: CommentAnchor | null;
  isChangeRequest?: boolean;
  /** "@이름" 을 이메일로 바꿔 멘션을 잡아내는 데 쓰는, 현재 활성 사용자 목록 */
  mentionDirectory?: MentionCandidate[];
}

export async function addComment(input: NewComment): Promise<CommentRow> {
  const body = input.body.trim();
  if (body === '') throw new Error('댓글 내용을 입력하세요.');

  const mentions = extractMentions(body, input.mentionDirectory ?? []);

  if (isDevPreview()) return devAddComment(input, body, mentions);

  const client = sb();
  const { data, error } = await client
    .from('cad_comment')
    .insert({
      version_id: input.versionId,
      parent_id: input.parentId ?? null,
      entity_id: input.entityId ?? null,
      anchor: input.anchor ?? null,
      body,
      mentions,
      is_change_request: input.isChangeRequest ?? false,
    })
    .select()
    .single();
  fail('댓글 작성', error);

  await logActivity('comment.create', {
    version: input.versionId,
    payload: { mentions, isChangeRequest: input.isChangeRequest ?? false },
  });

  return data as CommentRow;
}

export async function resolveComment(id: string, resolved: boolean): Promise<void> {
  if (isDevPreview()) {
    const c = DEV_COMMENTS.get(id);
    if (c) {
      c.status = resolved ? 'resolved' : 'open';
      c.resolved_by = resolved ? 'dev@localhost' : null;
      c.resolved_at = resolved ? new Date().toISOString() : null;
    }
    return;
  }

  const client = sb();
  const { error } = await client
    .from('cad_comment')
    .update({
      status: resolved ? 'resolved' : 'open',
      resolved_at: resolved ? new Date().toISOString() : null,
    })
    .eq('id', id);
  fail('댓글 상태 변경', error);
}

/** 열려 있는 수정 요청 수 — 승인 화면이 이 값으로 승인을 막는다 */
export function openChangeRequests(comments: CommentRow[]): CommentRow[] {
  return comments.filter((c) => c.is_change_request && c.status === 'open');
}

/* -------------------------------------------------------------------------- *
 * 승인 (P4-12)
 * -------------------------------------------------------------------------- */

/** 동작 → 목표 상태. 화면 버튼과 DB 기록이 같은 표를 본다. */
export const ACTION_TARGET: Record<ApprovalAction, VersionStatus> = {
  request: 'in_review',
  approve: 'approved',
  reject: 'changes_requested',
  changes_requested: 'changes_requested',
  publish: 'published',
  unpublish: 'archived',
  archive: 'archived',
};

export const ACTION_LABEL: Record<ApprovalAction, string> = {
  request: '검토 요청',
  approve: '승인',
  reject: '반려',
  changes_requested: '수정 요청',
  publish: '현장 배포',
  unpublish: '배포 해제',
  archive: '보관',
};

export interface GateResult {
  ok: boolean;
  reason?: string;
}

/**
 * 이 동작을 지금 할 수 있는가.
 *
 * ★ 승인 전에 **열린 수정 요청이 없어야 한다.**
 *   수정 요청을 남겨 둔 채 승인하면 그 요청은 영원히 묻힌다.
 *   실무에서 "분명히 코멘트 달았는데 그냥 승인됐다" 가 반복되는 지점이다.
 */
export function gateAction(
  action: ApprovalAction,
  from: VersionStatus,
  comments: CommentRow[],
  isReviewer: boolean
): GateResult {
  const to = ACTION_TARGET[action];

  if (!canTransition(from, to)) {
    return { ok: false, reason: `현재 상태(${from})에서는 할 수 없습니다.` };
  }

  if ((action === 'approve' || action === 'publish') && !isReviewer) {
    return { ok: false, reason: '검토자 이상 권한이 필요합니다.' };
  }

  if (action === 'approve') {
    const open = openChangeRequests(comments);
    if (open.length > 0) {
      return {
        ok: false,
        reason: `해결되지 않은 수정 요청이 ${open.length}건 있습니다. 먼저 처리하세요.`,
      };
    }
  }

  if (action === 'reject' || action === 'changes_requested') {
    // 사유 없는 반려는 받는 사람이 뭘 고쳐야 할지 알 수 없다 — 호출자가 채운다
    return { ok: true };
  }

  return { ok: true };
}

export async function listApprovals(versionId: string): Promise<ApprovalRow[]> {
  if (isDevPreview()) return [...DEV_APPROVALS.values()].filter((a) => a.version_id === versionId);

  const client = sb();
  const { data, error } = await client
    .from('cad_approval')
    .select('*')
    .eq('version_id', versionId)
    .order('created_at', { ascending: false });
  fail('승인 이력 조회', error);

  return (data ?? []) as ApprovalRow[];
}

/**
 * 상태를 바꾸고 이력을 남긴다.
 *
 * 순서가 중요하다: **이력을 먼저 남긴다.** 상태 변경이 실패해도 시도한
 * 기록은 남고, 반대로 하면 "상태는 바뀌었는데 누가 했는지 모르는" 상태가 된다.
 */
export async function performAction(
  versionId: string,
  action: ApprovalAction,
  from: VersionStatus,
  reason: string | null
): Promise<VersionStatus> {
  const to = ACTION_TARGET[action];

  if ((action === 'reject' || action === 'changes_requested') && !reason?.trim()) {
    throw new Error('반려·수정 요청에는 사유가 필요합니다.');
  }
  if (!canTransition(from, to)) {
    throw new Error(`${from} → ${to} 로는 바꿀 수 없습니다.`);
  }

  if (isDevPreview()) {
    const row: ApprovalRow = {
      id: `ap-${Date.now().toString(36)}`,
      version_id: versionId,
      action,
      from_status: from,
      to_status: to,
      reason: reason ?? null,
      actor: 'dev@localhost',
      created_at: new Date().toISOString(),
    };
    DEV_APPROVALS.set(row.id, row);
    return to;
  }

  const client = sb();

  const { error: eLog } = await client.from('cad_approval').insert({
    version_id: versionId,
    action,
    from_status: from,
    to_status: to,
    reason: reason ?? null,
  });
  fail('승인 이력 기록', eLog);

  const { error, count } = await client
    .from('cad_version')
    .update({ status: to }, { count: 'exact' })
    .eq('id', versionId)
    // 그 사이 남이 상태를 바꿨으면 0행 — 조용히 덮어쓰지 않는다
    .eq('status', from);
  fail('상태 변경', error);

  if (count === 0) {
    throw new Error('그 사이 다른 사람이 상태를 바꿨습니다. 새로고침 후 다시 시도하세요.');
  }

  await logActivity(`version.${action}`, {
    version: versionId,
    payload: { from, to, reason },
  });

  return to;
}

/* -------------------------------------------------------------------------- *
 * 개발 미리보기
 * -------------------------------------------------------------------------- */

const DEV_COMMENTS = new Map<string, CommentRow>();
const DEV_APPROVALS = new Map<string, ApprovalRow>();

function devComments(versionId: string): CommentRow[] {
  if (DEV_COMMENTS.size === 0) {
    const seed: CommentRow[] = [
      {
        id: 'c-1',
        version_id: versionId,
        parent_id: null,
        entity_id: null,
        anchor: { type: 'point', x: 8_000_000, y: 6_000_000 },
        body: '신선 코너 통로가 1.8m 로 보입니다. 카트 교행이 되나요? @reviewer.demo@trial-net.co.kr',
        mentions: ['reviewer.demo@trial-net.co.kr'],
        is_change_request: true,
        status: 'open',
        created_by: 'designer.demo@trial-net.co.kr',
        created_at: new Date(Date.now() - 3_600_000).toISOString(),
        resolved_by: null,
        resolved_at: null,
      },
      {
        id: 'c-2',
        version_id: versionId,
        parent_id: null,
        entity_id: null,
        anchor: null,
        body: '계산대 4대는 주말 피크에 부족합니다.',
        mentions: [],
        is_change_request: false,
        status: 'open',
        created_by: 'store.demo@trial-net.co.kr',
        created_at: new Date(Date.now() - 1_800_000).toISOString(),
        resolved_by: null,
        resolved_at: null,
      },
    ];
    for (const c of seed) DEV_COMMENTS.set(c.id, c);
  }
  return [...DEV_COMMENTS.values()]
    .filter((c) => c.version_id === versionId)
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
}

function devAddComment(input: NewComment, body: string, mentions: string[]): CommentRow {
  const row: CommentRow = {
    id: `c-${Date.now().toString(36)}`,
    version_id: input.versionId,
    parent_id: input.parentId ?? null,
    entity_id: input.entityId ?? null,
    anchor: input.anchor ?? null,
    body,
    mentions,
    is_change_request: input.isChangeRequest ?? false,
    status: 'open',
    created_by: 'dev@localhost',
    created_at: new Date().toISOString(),
    resolved_by: null,
    resolved_at: null,
  };
  DEV_COMMENTS.set(row.id, row);
  return row;
}
